import { trackEventWithObject } from '@/utils/gaDataPush';

/** GA4 eventAction for Track Order page (maps NS category FJ/Bridal). */
export function getTrackOrderEventAction(category?: string | null): 'Finished Jewelry' | 'Bridal' {
  return category === 'Bridal' ? 'Bridal' : 'Finished Jewelry';
}

export function trackTrackOrderEvent({
  category,
  eventLabel,
  eventInteraction = false,
}: {
  category?: string | null;
  eventLabel: string;
  eventInteraction?: boolean;
}) {
  if (typeof window === 'undefined') return;
  trackEventWithObject(
    {
      event: 'trackEvent',
      eventCategory: 'Track Order',
      eventAction: getTrackOrderEventAction(category),
      eventLabel,
    },
    eventInteraction
  );
}

export function trackTrackOrderFedExClick(
  category: string | null | undefined,
  kind: 'Show Full History' | 'Tracking Number' | 'Sign Up for Updates'
) {
  trackTrackOrderEvent({
    category,
    eventLabel: `Click to FedEx|${kind}`,
    eventInteraction: false,
  });
}

export function trackTrackOrderHeroBannerClick(category: string | null | undefined) {
  trackTrackOrderEvent({
    category,
    eventLabel: 'Hero Banner Click',
    eventInteraction: false,
  });
}

export function trackTrackOrderModuleClick({
  category,
  moduleIndex,
  clickTarget,
  moduleName,
  ctaText,
}: {
  category: string | null | undefined;
  moduleIndex: 1 | 2 | 3;
  clickTarget: 'CTA' | 'Image';
  moduleName: string;
  ctaText: string;
}) {
  const moduleLabel = String(moduleName || '').trim() || 'Untitled';
  const ctaLabel = String(ctaText || '').trim() || 'Untitled';
  trackTrackOrderEvent({
    category,
    eventLabel: `Module ${moduleIndex}|Click|${clickTarget}|${moduleLabel}|${ctaLabel}`,
    eventInteraction: false,
  });
}

type ContactUsChannel = 'Chat' | 'Call' | 'SMS' | 'Email';

export function resolveContactUsChannel(labelText?: string | null): ContactUsChannel | null {
  const label = String(labelText || '')
    .trim()
    .toLowerCase();
  if (!label) return null;
  if (label.includes('chat')) return 'Chat';
  if (label.includes('call')) return 'Call';
  if (label.includes('sms') || label.includes('text')) return 'SMS';
  if (label.includes('email')) return 'Email';
  return null;
}

/** Digits-only phone for GA eventLabel, e.g. tel:800.691.0952 → 8006910952 */
export function getPhoneDigitsForGaEvent(...candidates: Array<string | null | undefined>): string {
  const match = candidates
    .filter((candidate): candidate is string => Boolean(candidate))
    .map(candidate => {
      const telMatch = candidate.match(/tel:\s*([^?\s"'<>]+)/i);
      const raw = telMatch ? telMatch[1] : candidate;
      return raw.replace(/\D/g, '');
    })
    .find(digits => digits.length >= 10);

  return match || '';
}

/** Track Order Contact Us click + production parity events where required. */
export function trackTrackOrderContactUsClick({
  category,
  labelText,
  phoneCandidates,
}: {
  category?: string | null;
  labelText?: string | null;
  /** Builder LinkTile link / cta.href / description — used for Call eventLabel */
  phoneCandidates?: Array<string | null | undefined>;
}) {
  const channel = resolveContactUsChannel(labelText);
  if (!channel) return;

  trackTrackOrderEvent({
    category,
    eventLabel: `Contact Us|${channel}`,
    eventInteraction: true,
  });

  // Match production: Call also fires Mobile Click to Call + mobile_click_to_call
  if (channel === 'Call') {
    trackEventWithObject({ event: 'mobile_click_to_call' });
    const phoneLabel = getPhoneDigitsForGaEvent(...(phoneCandidates || []));
    if (phoneLabel) {
      trackEventWithObject({
        event: 'trackEvent',
        eventCategory: 'Mobile',
        eventAction: 'Click to Call',
        eventLabel: phoneLabel,
      });
    }
  }
  // Chat production parity (chat_now_click / disruptive) is fired by openDEChat('disruptive')
}
